import { keyframes } from '@emotion/react';
import { Box, type BoxProps } from '@mui/material';
import { forwardRef, type ReactNode } from 'react';

const enter = keyframes`
    from { opacity: 0; transform: translateY(4px); }
    to { opacity: 1; transform: none; }
`;

/**
 * How dense the panel is. A small panel has a 36 px header and tighter
 * padding, for options beside a tool; a medium one is the operation panel.
 */
export type PanelFrameSize = 'small' | 'medium';

interface PanelFrameProps extends Omit<BoxProps, 'children'> {
    /** The panel's header, such as a `PanelHeader`, over a rule. */
    header?: ReactNode;
    /**
     * Rows under the body that do not scroll with it, each with its own rule.
     * They mount only while the panel is not collapsed.
     */
    footer?: ReactNode;
    /** `medium` unless given. */
    size?: PanelFrameSize;
    /**
     * Shown in place of the header and the body while the panel is
     * collapsed. The body stays mounted, out of sight.
     */
    collapsed?: ReactNode;
    /** The body, which scrolls when the panel is shorter than it. */
    children: ReactNode;
}

/**
 * The look of a floating panel over the views: a raised surface with a
 * header and a body. It lays out nothing about where the panel is: the
 * caller places it through `sx`, which also overrides the surface.
 */
const PanelFrame = forwardRef<HTMLDivElement, PanelFrameProps>(
    function PanelFrame(
        { header, footer, size = 'medium', collapsed, children, sx, ...props },
        ref
    ) {
        const small = size === 'small';
        return (
            <Box
                ref={ref}
                {...props}
                sx={[
                    (theme) => ({
                        display: 'flex',
                        flexDirection: 'column',
                        boxSizing: 'border-box',
                        pointerEvents: 'auto',
                        bgcolor: 'background.raised',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        boxShadow: theme.shadows[8],
                        overflow: 'hidden',
                        animation: `${enter} 120ms ease-out`,
                    }),
                    ...(Array.isArray(sx) ? sx : [sx]),
                ]}
            >
                {collapsed ??
                    (header && (
                        // The header rule runs edge to edge, so the header
                        // carries its own padding and the body its own.
                        <Box
                            sx={{
                                ...(small
                                    ? {
                                          display: 'flex',
                                          alignItems: 'center',
                                          height: 36,
                                          pl: 1.5,
                                          pr: 0.5,
                                      }
                                    : { pl: 2, pr: 1.5, py: 1.5 }),
                                flexShrink: 0,
                                borderBottom: '1px solid',
                                borderColor: 'divider',
                            }}
                        >
                            {header}
                        </Box>
                    ))}
                <Box
                    sx={{
                        display: collapsed ? 'none' : 'block',
                        flex: '1 1 auto',
                        minHeight: 0,
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        p: small ? 1.5 : 2,
                        // The viewer turns selection off; typed values stay
                        // selectable.
                        userSelect: 'text',
                    }}
                >
                    {children}
                </Box>
                {!collapsed && footer}
            </Box>
        );
    }
);

export default PanelFrame;
